/* ═══════════════════════════════════════════
   JABAGIFT THEME — theme.js
   Cart AJAX · Engraving · Animations · UI
═══════════════════════════════════════════ */

(function () {
  'use strict';

  /* ── UTILITIES ── */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const on = (el, ev, fn, opts) => el?.addEventListener(ev, fn, opts);
  const emit = (el, ev, detail = {}) => el?.dispatchEvent(new CustomEvent(ev, { bubbles: true, detail }));

  function money(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function notify(msg, type = '') {
    let n = $('#notification');
    if (!n) {
      n = document.createElement('div');
      n.id = 'notification';
      n.className = 'notification';
      document.body.append(n);
    }
    n.textContent = msg;
    n.className = 'notification' + (type ? ` notification--${type}` : '');
    n.classList.add('show');
    clearTimeout(n._t);
    n._t = setTimeout(() => n.classList.remove('show'), 3500);
  }

  /* ── SCROLL ANIMATIONS ── */
  function initFadeAnimations() {
    const els = $$('.fade-up');
    if (!els.length) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e, i) => {
        if (e.isIntersecting) {
          setTimeout(() => e.target.classList.add('is-visible'), i * 80);
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
    els.forEach(el => obs.observe(el));
  }

  /* ── HEADER SCROLL ── */
  function initHeader() {
    const header = $('.site-header');
    if (!header) return;
    let last = 0;
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      header.classList.toggle('scrolled', y > 20);
      last = y;
    }, { passive: true });
  }

  /* ── HERO PARALLAX ── */
  function initHeroParallax() {
    const hero = $('.hero');
    const img = hero?.querySelector('.hero__img');
    if (!hero || !img) return;

    // Trigger loaded class for zoom-in effect
    requestAnimationFrame(() => hero.classList.add('loaded'));

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const y = window.scrollY;
          if (y < hero.offsetHeight * 1.5) {
            img.style.transform = `translateY(${y * 0.25}px) scale(1.0)`;
          }
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* ── MOBILE MENU ── */
  function initMobileMenu() {
    const toggle = $('.menu-toggle');
    const nav = $('.mobile-nav');
    const close = $('.mobile-nav__close');
    const overlay = $('#overlay');
    if (!toggle || !nav) return;

    function open() { nav.classList.add('open'); overlay.classList.add('active'); document.body.style.overflow = 'hidden'; }
    function closeFn() { nav.classList.remove('open'); overlay.classList.remove('active'); document.body.style.overflow = ''; }

    on(toggle, 'click', open);
    on(close, 'click', closeFn);
    on(overlay, 'click', closeFn);
  }

  /* ── CART DRAWER ── */
  const Cart = {
    drawer: null,
    body: null,
    footerEl: null,
    countEls: [],

    init() {
      this.drawer = $('.cart-drawer');
      this.body = $('.cart-drawer__body');
      this.footerEl = $('.cart-drawer__footer');
      this.countEls = $$('[data-cart-count]');
      const overlay = $('#overlay');

      // Open triggers
      $$('[data-open-cart]').forEach(btn => on(btn, 'click', () => this.open()));

      // Close
      const closeBtn = $('.cart-drawer__close');
      on(closeBtn, 'click', () => this.close());
      on(overlay, 'click', () => {
        if (this.drawer?.classList.contains('open')) this.close();
      });

      this.refresh();

      // ── Delegated handlers on body — survive every cart re-render ──
      on(this.body, 'click', async e => {
        // Remove item
        const removeBtn = e.target.closest('[data-remove]');
        if (removeBtn) { await this.updateItem(removeBtn.dataset.remove, 0); return; }

        // Qty − button
        const minusBtn = e.target.closest('[data-qty-minus]');
        if (minusBtn) {
          const valEl = minusBtn.parentElement?.querySelector('.jb-qty-val');
          const cur   = parseInt(valEl?.textContent || '1', 10);
          if (cur > 0) await this.updateItem(minusBtn.dataset.key, cur - 1);
          return;
        }

        // Qty + button
        const plusBtn = e.target.closest('[data-qty-plus]');
        if (plusBtn) {
          const valEl = plusBtn.parentElement?.querySelector('.jb-qty-val');
          const cur   = parseInt(valEl?.textContent || '1', 10);
          await this.updateItem(plusBtn.dataset.key, cur + 1);
          return;
        }

        // See Details toggle
        const dtlBtn = e.target.closest('[data-dtl]');
        if (dtlBtn) {
          const dtlEl = document.getElementById('dtl-' + dtlBtn.dataset.dtl);
          const open  = dtlBtn.classList.toggle('open');
          if (dtlEl) dtlEl.classList.toggle('open', open);
        }
      });
    },

    open() {
      this.refresh();
      this.drawer?.classList.add('open');
      $('#overlay')?.classList.add('active');
      document.body.style.overflow = 'hidden';
    },

    close() {
      this.drawer?.classList.remove('open');
      const anyDrawerOpen = $('.mobile-nav.open');
      if (!anyDrawerOpen) {
        $('#overlay')?.classList.remove('active');
        document.body.style.overflow = '';
      }
    },

    async refresh() {
      try {
        const r = await fetch('/cart.js');
        const cart = await r.json();
        this.updateCount(cart.item_count);
        this.render(cart);
      } catch (e) { console.error('Cart refresh error', e); }
    },

    updateCount(n) {
      this.countEls.forEach(el => {
        el.textContent = n;
        el.dataset.count = n;
      });
    },

    render(cart) {
      if (!this.body) return;
      if (cart.item_count === 0) {
        this.body.innerHTML = `
          <div class="cart-drawer__empty">
            <p>Your cart is empty.</p>
            <a href="/collections/all" class="link-arrow">Continue Shopping</a>
          </div>`;
        if (this.footerEl) this.footerEl.innerHTML = '';
        return;
      }

      /* ── edit-type lookup (metafield via Liquid-injected map + sessionStorage) ── */
      function getEditType(handle) {
        if (window.JabaProductMeta && window.JabaProductMeta[handle]) return window.JabaProductMeta[handle];
        try { const v = sessionStorage.getItem('jtEt:' + handle); if (v) return v; } catch(e) {}
        return 'none';
      }
      function escH(s) { return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;'); }

      const items = cart.items.map(item => {
        const editType = getEditType(item.handle || '');
        const safeKey  = item.key.replace(/[^a-z0-9]/gi, '_');

        const variantLine = (item.variant_title && item.variant_title !== 'Default Title')
          ? `<div class="jb-cart-item__variant">${escH(item.variant_title)}</div>` : '';

        /* See Details — shown for spotify_studio and simple_form types with properties */
        const propEntries = Object.entries(item.properties || {}).filter(([k, v]) => v && !k.startsWith('_'));
        const hasDetails  = (editType === 'spotify_studio' || editType === 'simple_form') && propEntries.length > 0;
        const ENGRAVE_KEYS = new Set(['Engraving Text', 'Engraving Info', 'Personalization']);
        const detailsHTML = propEntries.map(([k, v]) => {
          const displayKey = ENGRAVE_KEYS.has(k) ? 'Personalization' : k;
          // No truncation — CSS max-height clips overflow; white-space:pre-line preserves \n
          return `<p class="jb-cart-item__detail"><span class="jb-cart-item__detail-key">${escH(displayKey)}:</span> ${escH(String(v))}</p>`;
        }).join('');
        const detailsSection = hasDetails ? `
          <button class="jb-cart-item__see-details" type="button" data-dtl="${safeKey}">
            See Details <span class="jb-cart-item__chevron">▾</span>
          </button>
          <div class="jb-cart-item__details-body" id="dtl-${safeKey}">${detailsHTML}</div>` : '';

        const editLink = (editType !== 'none' && item.handle)
          ? `<a href="/products/${item.handle}?edit_cart_key=${encodeURIComponent(item.key)}&variant=${item.variant_id}" class="jb-cart-item__edit-link">Edit</a>`
          : '';

        return `
          <div class="jb-cart-item" data-line="${item.key}">
            <a href="${item.url}" class="jb-cart-item__img-wrap">
              <img class="jb-cart-item__img" src="${item.image}" alt="${escH(item.title)}" loading="lazy">
            </a>
            <div class="jb-cart-item__info">
              <div class="jb-cart-item__header">
                <a href="${item.url}" class="jb-cart-item__name">${escH(item.product_title)}</a>
                <button class="jb-cart-item__remove" data-remove="${item.key}" aria-label="Remove item" type="button">×</button>
              </div>
              ${variantLine}
              ${detailsSection}
              <div class="jb-cart-item__foot">
                <div>${editLink}</div>
                <div class="jb-cart-item__foot-right">
                  <span class="jb-cart-item__price">${money(item.final_line_price)}</span>
                  <div class="jb-cart-item__qty">
                    <button class="jb-qty-btn" type="button" data-qty-minus data-key="${item.key}">−</button>
                    <span class="jb-qty-val">${item.quantity}</span>
                    <button class="jb-qty-btn" type="button" data-qty-plus data-key="${item.key}">+</button>
                  </div>
                </div>
              </div>
            </div>
          </div>`;

      }).join('');


      this.body.innerHTML = items;
      // Handlers are delegated on this.body in init() — no per-element binding needed here

      if (this.footerEl) {
        const FREE_SHIP = 3500;
        const remaining = FREE_SHIP - cart.total_price;
        const pct = Math.min(100, Math.round((cart.total_price / FREE_SHIP) * 100));
        const barHTML = remaining > 0
          ? `<div class="shipping-bar"><p>You're <strong>${money(remaining)}</strong> away from free shipping!</p><div class="shipping-bar__track"><div class="shipping-bar__fill" style="width:${pct}%"></div></div></div>`
          : `<div class="shipping-bar shipping-bar--done"><p>✓ You qualify for free shipping!</p></div>`;
        this.footerEl.innerHTML = `
          ${barHTML}
          <div class="drawer-subtotal"><span>Subtotal</span><strong>${money(cart.total_price)}</strong></div>
          <a href="/checkout" class="btn-atc" style="text-decoration:none;display:block;text-align:center;">Checkout</a>
          <a href="/cart" class="btn-secondary" style="text-decoration:none;display:block;text-align:center;margin-top:10px;">View Cart</a>
          <p class="cart-note">Shipping &amp; taxes calculated at checkout</p>`;
      }
    },

    async add(data) {
      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(data)
        });
        if (!r.ok) throw new Error('Add to cart failed');
        await this.refresh();
        this.open();
        notify('Added to cart!', 'success');
      } catch (e) {
        notify('Sorry, something went wrong.', 'error');
        console.error(e);
      }
    },

    async updateItem(key, qty) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty })
        });
        await this.refresh();
      } catch (e) { console.error(e); }
    }
  };

  /* ── PRODUCT PAGE ── */
  function initProductPage() {
    const form = $('form[action="/cart/add"]') || $('#pd-form');
    if (!form) return;

    // ── Gallery ──
    initGallery();

    // ── Variant Selection ──
    initVariants(form);

    // ── Quantity ──
    initQuantity(form);

    // ── Engraving Preview ──
    initEngravingPreview(form);

    // ── Add to Cart ──
    initAddToCart(form);
  }

  function initGallery() {
    const mainImg = $('.product-gallery__main-img');
    const thumbs = $$('.product-gallery__thumb');
    if (!mainImg || !thumbs.length) return;

    thumbs.forEach(thumb => {
      on(thumb, 'click', () => {
        const src = thumb.dataset.src;
        const srcset = thumb.dataset.srcset;
        mainImg.style.opacity = '0';
        setTimeout(() => {
          mainImg.src = src;
          if (srcset) mainImg.srcset = srcset;
          mainImg.style.opacity = '1';
        }, 220);
        thumbs.forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
      });
    });
  }

  function initVariants(form) {
    const variantId = $('[name="id"]', form);
    const priceEl = $('.product-details__price');
    const atcBtn = $('.btn-atc', form);
    const availabilityEl = $('.product-availability');

    // Swatch variant buttons
    $$('.btn-variant', form).forEach(btn => {
      on(btn, 'click', () => {
        const group = btn.closest('.btn-variant-group');
        $$('.btn-variant', group).forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        // Find matching variant
        updateSelectedVariant(form, variantId, priceEl, atcBtn, availabilityEl);
      });
    });

    // Select dropdown variants
    $$('.variant-select', form).forEach(sel => {
      on(sel, 'change', () => {
        updateSelectedVariant(form, variantId, priceEl, atcBtn, availabilityEl);
      });
    });
  }

  function updateSelectedVariant(form, variantId, priceEl, atcBtn, availEl) {
    // Collect current option selections
    const options = [];
    $$('.btn-variant-group', form).forEach(group => {
      const active = $('.btn-variant.active', group);
      if (active) options.push(active.dataset.value);
    });
    $$('.variant-select', form).forEach(sel => {
      options.push(sel.value);
    });

    // Find variant in embedded JSON
    const variantsEl = $('#product-variants-json');
    if (!variantsEl) return;
    const variants = JSON.parse(variantsEl.textContent);

    const match = variants.find(v => {
      return options.every((opt, i) => v.options[i] === opt);
    });

    if (match) {
      if (variantId) variantId.value = match.id;
      if (priceEl) {
        if (match.compare_at_price && match.compare_at_price > match.price) {
          priceEl.innerHTML = `<span class="sale-price">${money(match.price)}</span><s>${money(match.compare_at_price)}</s>`;
        } else {
          priceEl.textContent = money(match.price);
        }
      }
      if (atcBtn) {
        atcBtn.disabled = !match.available;
        atcBtn.textContent = match.available ? 'Add to Cart' : 'Sold Out';
      }
      // Update main image if variant has image
      if (match.featured_image) {
        const mainImg = $('.product-gallery__main-img');
        if (mainImg) {
          mainImg.style.opacity = '0';
          setTimeout(() => { mainImg.src = match.featured_image.src; mainImg.style.opacity = '1'; }, 200);
        }
      }
    }
  }

  function initQuantity(form) {
    const input = $('.qty-input', form);
    const minus = $('.qty-btn--minus', form);
    const plus = $('.qty-btn--plus', form);
    if (!input) return;

    on(minus, 'click', () => {
      const v = parseInt(input.value) || 1;
      if (v > 1) input.value = v - 1;
    });
    on(plus, 'click', () => {
      const v = parseInt(input.value) || 1;
      input.value = v + 1;
    });
    on(input, 'change', () => {
      if (parseInt(input.value) < 1) input.value = 1;
    });
  }

  function initEngravingPreview(form) {
    const textarea = $('.engraving-text-input', form);
    const preview = $('.engraving-preview', form);
    const previewText = $('.engraving-preview__text', form);
    if (!textarea || !preview) return;

    on(textarea, 'input', () => {
      const val = textarea.value.trim();
      if (val) {
        preview.classList.add('visible');
        if (previewText) previewText.textContent = val;
      } else {
        preview.classList.remove('visible');
      }
    });
  }

  function initAddToCart(form) {
    // The current product template owns its submit handler.
    if (form.id === 'pdp-form') return;
    on(form, 'submit', async (e) => {
      e.preventDefault();
      const btn = $('.btn-atc', form) || $('.pd-atc', form);
      if (btn?.disabled) return;

      /* ── ENGRAVING VALIDATION (v3-19 addition) ── */
      const engBtns = $('#pd-engrave-btns', form) || $('[data-option*="personali"]', form) || $('[data-option*="engrav"]', form);
      const engPanel = $('#pd-engrave-panel');
      const engText = $('#pd-engrave-text', form) || $('[name="properties[Engraving Text]"]', form);
      
      if (engBtns && engPanel && engText) {
        var activeBtn = engBtns.querySelector('.pd-btn.active, .btn-variant.active');
        if (activeBtn) {
          var val = (activeBtn.dataset.val || activeBtn.dataset.value || activeBtn.textContent || '').toLowerCase();
          var isNo = val.includes('no engrav') || val.includes('no personali') || val.includes('without') || val === 'no personalization';
          if (!isNo && !engText.value.trim()) {
            engText.classList.add('err');
            engText.style.borderColor = '#c0392b';
            engText.focus();
            notify('Please enter your engraving text.', 'error');
            var errMsg = $('#pd-engrave-err');
            if (errMsg) errMsg.classList.add('show');
            return;
          }
        }
      }
      if (engText) { engText.classList.remove('err'); engText.style.borderColor = ''; }
      var errMsg2 = $('#pd-engrave-err');
      if (errMsg2) errMsg2.classList.remove('show');
      /* ── END ENGRAVING VALIDATION ── */

      // Validate engraving (legacy fallback)
      const engravingSelect = $('[name="properties[Engraving Option]"]', form);
      const engravingTextLegacy = $('[name="properties[Engraving Text]"]', form);
      const engravingRequired = engravingSelect?.dataset.required === 'true';

      if (engravingRequired && engravingSelect && engravingSelect.value !== 'No Engraving' && engravingTextLegacy && !engravingTextLegacy.value.trim()) {
        engravingTextLegacy.style.borderColor = 'var(--error)';
        engravingTextLegacy.focus();
        notify('Please enter your engraving text.', 'error');
        return;
      }
      if (engravingTextLegacy) engravingTextLegacy.style.borderColor = '';

      const qty = parseInt($('.qty-input', form)?.value || $('#pd-qty', form)?.value) || 1;
      const id = parseInt($('[name="id"]', form)?.value);
      if (!id) return;

      // Build properties
      const properties = {};
      $$('[name^="properties["]', form).forEach(el => {
        const key = el.name.match(/\[(.+)\]/)?.[1];
        if (key && el.value.trim()) properties[key] = el.value.trim();
      });

      // Loading state
      if (btn) {
        btn.classList.add('loading');
        btn.textContent = 'Adding...';
        btn.disabled = true;
      }

      await Cart.add({ id, quantity: qty, properties });

      // Reset
      if (btn) {
        btn.classList.remove('loading');
        btn.disabled = false;
        btn.textContent = 'Add to Cart';
      }

      // Update sticky ATC price if visible
      const stickyPrice = $('#sticky-price');
      const _priceEl = $('#pd-price');
      if (stickyPrice && _priceEl) stickyPrice.textContent = _priceEl.textContent;
    });
  }

  /* ── ACCORDION ── */
  function initAccordions() {
    $$('.accordion__trigger').forEach(trigger => {
      on(trigger, 'click', () => {
        const body = trigger.nextElementSibling;
        const isOpen = trigger.classList.contains('open');
        // Close all in same accordion
        const accordion = trigger.closest('.accordion');
        $$('.accordion__trigger', accordion).forEach(t => {
          t.classList.remove('open');
          t.nextElementSibling?.classList.remove('open');
        });
        if (!isOpen) {
          trigger.classList.add('open');
          body?.classList.add('open');
        }
      });
    });
  }

  /* ── CART PAGE ── */
  function initCartPage() {
    $$('[data-qty-change]').forEach(input => {
      on(input, 'change', async () => {
        const key = input.dataset.key;
        const qty = parseInt(input.value) || 0;
        await Cart.updateItem(key, qty);
        location.reload();
      });
    });
  }

  /* ── SORT/FILTER ── */
  function initCollectionSort() {
    const sel = $('.sort-select');
    if (!sel) return;
    on(sel, 'change', () => {
      const url = new URL(location.href);
      url.searchParams.set('sort_by', sel.value);
      location.href = url.toString();
    });
  }

  /* ── INIT ALL ── */
  document.addEventListener('DOMContentLoaded', () => {
    initFadeAnimations();
    initHeader();
    initHeroParallax();
    initMobileMenu();
    Cart.init();
    initProductPage();
    initAccordions();
    initCartPage();
    initCollectionSort();
  });

})();
